import { DataTable } from "primereact/datatable";
import {
  emailOrderColumns,
  rowExpansionEmailOrderLineItemsColumns
} from "../../constants/table-columns/tableColumns";
import { useEffect, useMemo, useRef, useState, useContext } from "react";
import * as Yup from "yup";
import Spinner from "../../components/spinner/spinner";
import {
  getEmailOrders,
  approveEmailOrders,
  rejectEmailOrders,
  getEmailContent,
  getEmailOrderTradingPartners,
  getTradingPartnersList,
  editPdfOrderItems
} from "../../services/EmailOrderService";
import { Toast } from "primereact/toast";
import * as constants from "../../constants/constants";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import {
  formatDate,
  formatDateTime,
  formatDateAndTimeFromUtcToPst,
  emailOrdersApprovalStatusOptions,
  emailOrdersApprovalStatusKeyValueOptions,
  emailOrdersD365StatusOptions,
  orderTypeOptions,
  emailBodyOrderTradingPartners,
  pdfOrderTradingPartners
} from "../../constants/constants";
import { Tag } from "primereact/tag";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import styles from "./EmailOrder.module.scss";
import { Form, Formik } from "formik";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Calendar } from "primereact/calendar";
import { Checkbox } from "primereact/checkbox";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import * as actionCreators from "../../store/action-creators";
import { sortData } from "../../constants/dataSort";
import LoginUserDataContext from "../../store/login";
import { useFormik } from "formik";
import { appInsights } from "../../AppInsights";
import { reProcessSevenElevenOrders } from "../../services/logSummary";

const EmailOrder = (props) => {
  const { orderType, isReprocessBtnEnabled } = props;
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageRows, setCurrentPageRows] = useState(10);
  const toast = useRef(null);
  const [disableFields, setDisableFields] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const [userData, setUserData] = useContext(LoginUserDataContext);
  const [expandedRows, setExpandedRows] = useState(null);
  const [allExpanded, setAllExpanded] = useState(false);
  const [selectedEmailOrders, setSelectedEmailOrders] = useState([]);
  const [tradingPartners, setTradingPartners] = useState([]);
  const [emailOrderData, setEmailOrderData] = useState([]);
  const [emailOrderAction, setEmailOrderAction] = useState(null);
  const [emailContent, setEmailContent] = useState(null);
  const [showReProcessConfirmation, setShowReprocesConfirmation] =
    useState(false);
  const [defaultRegion, setDefaultRegion] = useState([]);
  const [editingOrderId, setEditingOrderId] = useState(null);
  const [originalLineItems, setOriginalLineItems] = useState({});
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);

  const [
    approveOrRejectOrderConfirmDialog,
    setApproveOrRejectOrderConfirmDialog
  ] = useState(false);

  const [emailContentModalVisible, setEmailContentModalVisible] =
    useState(false);

  const emailOrdersApprovalStatusFilterDropdownOptions = [
    { label: "All", value: emailOrdersApprovalStatusOptions.All },
    { label: "Pending", value: emailOrdersApprovalStatusOptions.Pending },
    {
      label: "Processed",
      value: emailOrdersApprovalStatusOptions.Processed
    },
    {
      label: "Failed",
      value: emailOrdersApprovalStatusOptions.Failed
    },
    {
      label: "Rejected",
      value: emailOrdersApprovalStatusOptions.Rejected
    }
  ];

  const dispatch = useDispatch();
  const { getEmailOrderRegions, getRegions } = bindActionCreators(
    actionCreators,
    dispatch
  );
  const emailOrderRegionList = useSelector((state) => {
    const data = state.orderReducer.emailOrderRegions;

    const transformedRegions = data.map((region) => {
      return {
        label: region.regionName + " (" + region.regionCode + ")",
        value: region.regionCode
      };
    });

    return transformedRegions;
  });

  const regions = useSelector((state) => {
    const data = state?.orderReducer?.regions;
    const sortedData = sortData("regionNameAbbr", data);
    return sortedData;
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    const params = {
      // emailOrderType: orderType
      tradingPartnerValue: 1
    };
    getEmailOrderRegions({ params });
    getRegions();
  }, []);

  const fetchTradingPartnersList = async (regionCode) => {
    try {
      const result = await getTradingPartnersList(regionCode, orderType);
      setTradingPartners(result);
    } catch (error) {
      setTradingPartners([]);
      setShowSpinner(false);
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo:
            "Error while Fetching Trading Partner list in Email/Pdf order form"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
  };

  const fetchEmailOrders = async (formBody) => {
    try {
      setShowSpinner(true);
      formBody = {
        purchaseOrderNumber: formBody?.purchaseOrderNumber
          ? formBody?.purchaseOrderNumber
          : null,
        tradingPartnerName: formBody?.tradingPartnerName
          ? formBody?.tradingPartnerName
          : null,
        approvalStatus: formBody?.approvalStatus
          ? formBody?.approvalStatus
          : null,
        requestedShippingStartDate: formBody?.requestedShippingStartDate
          ? formatDate(formBody?.requestedShippingStartDate)
          : null,
        requestedShippingEndDate: formBody?.requestedShippingEndDate
          ? formatDate(formBody?.requestedShippingEndDate)
          : null,
        emailOrderType: orderType,
        region: formBody?.region ? formBody?.region?.regionNameAbbr : null
      };

      let result = await getEmailOrders(formBody);

      setEmailOrderData(result);

      setShowSpinner(false);
    } catch (error) {
      setShowSpinner(false);
      setEmailOrderData([]);

      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
    }
  };

  const orderActionTemplate = (rowData) => {
    if (!(isReprocessBtnEnabled && rowData?.isEligibleForReprocess)) {
      return;
    }
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-refresh"
          rounded
          text
          raised
          severity="secondary"
          aria-label="Reprocess"
          disabled={!rowData?.isEligibleForReprocess}
          onClick={() => onReProcessAllHandler([rowData])}
          tooltip="Reprocess Order"
        />
        <Button
          icon="pi pi-pencil"
          rounded
          text
          severity="secondary"
          onClick={() => {
            const newEditingId =
              editingOrderId === rowData.id ? null : rowData.id;
            setEditingOrderId(newEditingId);
            if (newEditingId) {
              setOriginalLineItems((prev) => ({
                ...prev,
                [newEditingId]: JSON.parse(
                  JSON.stringify(rowData.orderLineItems)
                )
              }));
            }
          }}
          tooltip="Edit Order Line Items"
        />
        <Button
          icon="pi pi-save"
          rounded
          text
          severity="secondary"
          onClick={() => {
            if (editingOrderId) {
              setShowSaveConfirmation(true);
            }
          }}
          disabled={editingOrderId !== rowData.id}
        />
      </div>
    );
  };

  const onReProcessAllHandler = (rowData) => {
    let records = rowData
      ?.filter((x) => x?.isEligibleForReprocess)
      .map((x) => x?.id);
    if (!records.length) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail: "No Failed Orders found",
        life: 3000
      });
      return;
    }
    setSelectedEmailOrders(records);
    setShowReprocesConfirmation(true);
  };
  const reprocessItem = async () => {
    try {
      setShowSpinner(true);
      let result = await reProcessSevenElevenOrders(
        selectedEmailOrders,
        formik.values.region?.regionNameAbbr
      );
      if (!!result) {
        await fetchEmailOrders({
          ...formik?.values,
          emailOrderType: orderType
        });

        toast.current.show({
          severity: "success",
          summary: "Success",
          detail: result,
          life: 4000
        });
      }
      setShowSpinner(false);
    } catch (error) {
      setSelectedEmailOrders([]);
      setShowReprocesConfirmation(false);
      await fetchEmailOrders({
        ...formik?.values,
        emailOrderType: orderType
      });
      setShowSpinner(false);
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: "Exception while Reprocess all Pdf orders API"
        },
        measurements: {
          severityLevel: 2
        }
      });
    }
  };

  const saveEditedItems = async () => {
    try {
      setShowSpinner(true);
      const currentOrder = emailOrderData.find(
        (order) => order?.id === editingOrderId
      );
      const originalItems = originalLineItems[editingOrderId] || [];
      const currentItems = currentOrder?.orderLineItems || [];
      let validationError = null;
      if (!currentOrder?.purchaseOrderNumber?.trim()) {
        validationError = "PO Number cannot be empty.";
      } else if (currentOrder.purchaseOrderNumber.includes(" ")) {
        validationError = "PO Number cannot contain spaces.";
      }
      currentItems.forEach((item) => {
        if (!validationError) {
          if (
            !item?.buyersItemId?.trim() ||
            !item?.quantity?.toString().trim()
          ) {
            validationError =
              "Buyers Item ID and Quantity cannot be empty. Please fill in all fields.";
          } else if (
            !/^\d+$/.test(item?.quantity?.toString().trim()) ||
            Number(item?.quantity) < 0
          ) {
            validationError =
              "Quantity must be a whole number. Decimals, special characters, spaces are not allowed.";
          } else if (
            item?.buyersItemId?.includes(" ") ||
            item?.quantity?.toString().includes(" ")
          ) {
            validationError =
              "Buyers Item ID and Quantity cannot contain spaces.";
          }
        }
      });
      if (validationError) {
        toast?.current?.show({
          severity: "error",
          summary: "Validation Error",
          detail: validationError,
          life: 4000
        });
        setShowSaveConfirmation(false);
        setShowSpinner(false);
        return;
      }
      const lineItems = [];
      currentItems.forEach((currentItem, index) => {
        const originalItem = originalItems[index];
        if (
          originalItem &&
          (originalItem?.buyersItemId !== currentItem?.buyersItemId ||
            originalItem?.quantity !== currentItem?.quantity)
        ) {
          lineItems.push({
            id: currentItem?.id,
            oldBuyerItemId: originalItem?.buyersItemId,
            newBuyerItemId: currentItem?.buyersItemId,
            quantity: parseInt(currentItem?.quantity)
          });
        }
      });
      const payload = {
        purchaseOrderNumber: currentOrder.purchaseOrderNumber,
        shippingDate: currentOrder.requestedShippingDate,
        receiptDate: currentOrder.requestedReceiptDate,
        lineItems
      };
      if (
        lineItems.length > 0 ||
        currentOrder.purchaseOrderNumber ||
        currentOrder.requestedShippingDate ||
        currentOrder.requestedReceiptDate
      ) {
        const response = await editPdfOrderItems(editingOrderId, payload);
        if (response?.statusCode == 200) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: response?.result,
            life: 4000
          });
          setOriginalLineItems((prev) => ({
            ...prev,
            [editingOrderId]: JSON.parse(
              JSON.stringify(currentOrder.orderLineItems)
            )
          }));
        }
        if (response?.statusCode == 400) {
          toast.current.show({
            severity: "error",
            summary: "Error",
            detail: response?.message,
            life: 5000
          });
        }
      }
    } catch (error) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: error.message,
        life: 4000
      });
      appInsights.trackException({
        exception: error,
        properties: {
          additionalInfo: "Exception while saving edited order items"
        },
        measurements: {
          severityLevel: 2
        }
      });
    } finally {
      setShowSaveConfirmation(false);
      setShowSpinner(false);
      await fetchEmailOrders({
        ...formik?.values,
        emailOrderType: orderType
      });
      setEditingOrderId(null);
    }
  };

  const buyersItemIdBody = (rowData, options) => {
    if (editingOrderId === options?.data?.id) {
      return (
        <InputText
          value={rowData?.buyersItemId || ""}
          onChange={(e) => {
            const updatedData = [...emailOrderData];
            const orderIndex = updatedData.findIndex(
              (order) => order?.id === options?.data?.id
            );
            if (orderIndex !== -1) {
              const itemIndex = updatedData[
                orderIndex
              ].orderLineItems.findIndex((item) => item === rowData);
              if (itemIndex !== -1) {
                updatedData[orderIndex].orderLineItems[itemIndex] = {
                  ...rowData,
                  buyersItemId: e.target.value
                };
                setEmailOrderData(updatedData);
              }
            }
          }}
        />
      );
    }
    return rowData.buyersItemId;
  };

  const quantityBody = (rowData, options) => {
    if (editingOrderId === options.data.id) {
      return (
        <InputText
          value={rowData.quantity ?? ""}
          onChange={(e) => {
            const updatedData = [...emailOrderData];
            const orderIndex = updatedData.findIndex(
              (order) => order.id === options.data.id
            );
            if (orderIndex !== -1) {
              const itemIndex = updatedData[
                orderIndex
              ].orderLineItems.findIndex((item) => item === rowData);
              if (itemIndex !== -1) {
                updatedData[orderIndex].orderLineItems[itemIndex] = {
                  ...rowData,
                  quantity: e.target.value ?? 0
                };
                setEmailOrderData(updatedData);
              }
            }
          }}
        />
      );
    }
    return rowData.quantity;
  };

  const poNumberBody = (rowData) => {
    if (editingOrderId === rowData?.id) {
      return (
        <InputText
          value={rowData.purchaseOrderNumber || ""}
          onChange={(e) => {
            const updatedData = [...emailOrderData];
            const index = updatedData.findIndex(
              (item) => item?.id === rowData?.id
            );

            if (index !== -1) {
              updatedData[index] = {
                ...updatedData[index],
                purchaseOrderNumber: e?.target?.value
              };

              setEmailOrderData(updatedData);
            }
          }}
        />
      );
    }
    return rowData.purchaseOrderNumber;
  };

  const requestedShippingDateBody = (rowData) => {
    if (editingOrderId === rowData.id) {
      return (
        <Calendar
          value={
            rowData.requestedShippingDate
              ? new Date(rowData.requestedShippingDate)
              : null
          }
          onChange={(e) => {
            const updatedData = [...emailOrderData];
            const index = updatedData.findIndex(
              (item) => item.id === rowData.id
            );

            if (index !== -1) {
              updatedData[index] = {
                ...updatedData[index],
                requestedShippingDate: constants.convertPstDateToUtcIso(e.value)
              };

              setEmailOrderData(updatedData);
            }
          }}
          showIcon
        />
      );
    }
    return formatDateTime(rowData.requestedShippingDate);
  };

  const requestedReceiptDateBody = (rowData) => {
    if (editingOrderId === rowData?.id) {
      return (
        <Calendar
          value={
            rowData.requestedReceiptDate
              ? new Date(rowData.requestedReceiptDate)
              : null
          }
          onChange={(e) => {
            const updatedData = [...emailOrderData];
            const index = updatedData.findIndex(
              (item) => item?.id === rowData?.id
            );

            if (index !== -1) {
              updatedData[index] = {
                ...updatedData[index],
                requestedReceiptDate: constants.convertPstDateToUtcIso(e.value)
              };

              setEmailOrderData(updatedData);
            }
          }}
          showIcon
        />
      );
    }
    return formatDateTime(rowData.requestedReceiptDate);
  };

  const rowExpansionTemplate = (data) => {
    return (
      <div className="p-1 pl-5 ml-5" style={{ width: "30%", minWidth: "5rem" }}>
        <DataTable
          value={data.orderLineItems}
          emptyMessage={constants.emptyMessage}
          scrollable={constants.scrollable}
        >
          {rowExpansionEmailOrderLineItemsColumns.map((col, index) => (
            <Column
              key={index}
              field={col.field}
              header={col.header}
              sortable={col.sort}
              style={{ minWidth: col.minWidth }}
              body={
                col.field === "buyersItemId"
                  ? (rowData) => buyersItemIdBody(rowData, { data })
                  : col.field === "quantity"
                    ? (rowData) => quantityBody(rowData, { data })
                    : col.value
              }
            />
          ))}
        </DataTable>
      </div>
    );
  };

  const toggleRows = () => {
    if (allExpanded) {
      setExpandedRows(null);
      setAllExpanded(false);
    } else {
      let newExpandedRows = {};
      emailOrderData.forEach((p) => (newExpandedRows[`${p.id}`] = true));
      setExpandedRows(newExpandedRows);
      setAllExpanded(true);
    }
  };

  const requestedShippingDateFormat = (rowData) => {
    return formatDateTime(rowData.requestedShippingDate);
  };

  const requestedReceiptDateFormat = (rowData) => {
    return formatDateTime(rowData.requestedReceiptDate);
  };

  const actionTakenDateUTCFormat = (rowData) => {
    return formatDateAndTimeFromUtcToPst(rowData.actionTakenDateUTC);
  };

  const emailReceivedTimeFormat = (rowData) => {
    return formatDateAndTimeFromUtcToPst(rowData.emailRecievedDateTime);
  };

  const processingStatusFormat = (rowData) => {
    let severityValue = "";
    switch (rowData.approvalStatus) {
      case emailOrdersApprovalStatusOptions.Approved:
        severityValue = "success";
        break;
      case emailOrdersApprovalStatusOptions.Rejected:
        severityValue = "danger";
        break;
      case emailOrdersApprovalStatusOptions.Pending:
        severityValue = "secondary";
        break;
      case emailOrdersApprovalStatusOptions.Submitted:
        severityValue = "info";
        break;
      case emailOrdersApprovalStatusOptions.Processed:
        severityValue = "info";
        break;
      case emailOrdersApprovalStatusOptions.Failed:
        severityValue = "danger";
        break;
      case emailOrdersApprovalStatusOptions.Cancelled:
        severityValue = "info";
        break;
      default:
        severityValue = "";
        break;
    }

    return (
      <div>
        <Tag
          severity={severityValue}
          value={
            emailOrdersApprovalStatusKeyValueOptions[rowData.approvalStatus]
          }
          rounded
        ></Tag>
      </div>
    );
  };

  const fetchEmailContent = async (emailContentRequestObject) => {
    let response;

    try {
      setShowSpinner(true);

      response = await getEmailContent(emailContentRequestObject);
      const blob = response.data;

      if (orderType === orderTypeOptions.EmailBody) {
        const htmlContent = await blob.text();

        setEmailContent(htmlContent);
      } else if (orderType === orderTypeOptions.PDF) {
        const blobUrl = URL.createObjectURL(blob);

        if (emailContent) {
          URL.revokeObjectURL(emailContent);
        }

        setEmailContent(blobUrl);
      }

      setShowSpinner(false);
      setEmailContentModalVisible(true);
    } catch (error) {
      setShowSpinner(false);

      toast.current.show({
        severity: "error",
        summary: "Content Request Failed",
        detail: error.message,
        life: 3000
      });
    }
  };

  const emailContentFormat = (rowData) => {
    return (
      <Button
        icon={
          orderType === orderTypeOptions.EmailBody
            ? "pi pi-envelope"
            : orderTypeOptions.PDF
              ? "pi pi-file-pdf"
              : null
        }
        rounded
        text
        aria-label="Filter"
        severity="info"
        className={styles.emailContentBtn}
        onClick={() =>
          fetchEmailContent({
            purchaseOrderId: rowData.id
          })
        }
      ></Button>
    );
  };

  const onHideEmailContentModal = () => {
    if (emailContent) {
      URL.revokeObjectURL(emailContent);
      setEmailContent(null);
    }

    setEmailContentModalVisible(false);
  };

  const approveOrRejectEmailOrdersHandler = (action) => {
    if (selectedEmailOrders.length === 0) {
      toast.current.show({
        severity: "warn",
        summary: "Warning",
        detail:
          "Please Select atleast one order to " +
          (action === emailOrdersApprovalStatusOptions.Approved
            ? "approve"
            : "reject"),
        life: 3000
      });

      return;
    }

    setEmailOrderAction(action);
    setApproveOrRejectOrderConfirmDialog(true);
  };

  const approveOrRejectEmailOrdersAccept = async () => {
    let response;

    try {
      setShowSpinner(true);

      emailOrderAction === emailOrdersApprovalStatusOptions.Approved
        ? (response = await approveEmailOrders({
            purchaseOrderIds: selectedEmailOrders
          }))
        : (response = await rejectEmailOrders({
            purchaseOrderIds: selectedEmailOrders
          }));

      setSelectedEmailOrders([]);
      setEmailOrderAction(null);

      await fetchEmailOrders({
        ...formik?.values,
        emailOrderType: orderType
      });

      setShowSpinner(false);

      if (response?.statusCode === 200) {
        toast.current.show({
          severity: "success",
          summary: "Success",
          detail:
            (emailOrderAction === emailOrdersApprovalStatusOptions.Approved
              ? "Approved"
              : "Rejected") + " successfully.",
          life: 3000
        });
      } else if (response?.statusCode) {
        toast.current.show({
          severity: "error",
          summary:
            (emailOrderAction === emailOrdersApprovalStatusOptions.Approved
              ? "Approve"
              : "Reject") + " Failed",
          detail: response.result,
          life: 3000
        });
      }
    } catch (error) {
      setShowSpinner(false);
      toast.current.show({
        severity: "error",
        summary:
          (emailOrderAction === emailOrdersApprovalStatusOptions.Approved
            ? "Approve"
            : "Reject") + " Failed",
        detail: error.message,
        life: 3000
      });
    } finally {
      setShowSpinner(false);
    }
  };

  const approveOrRejectEmailOrdersReject = () => {
    setEmailOrderAction(null);
    setApproveOrRejectOrderConfirmDialog(false);
  };

  const dataTableFooter = (
    <div className="flex justify-content-start p-3">
      <div className="flex gap-2">
        <Button
          icon="pi pi-check"
          label="Approve"
          size="small"
          onClick={() =>
            approveOrRejectEmailOrdersHandler(
              emailOrdersApprovalStatusOptions.Approved
            )
          }
          autoFocus
          className={styles.approveBtn}
        />
        <Button
          icon="pi pi-times"
          label="Reject"
          size="small"
          onClick={() =>
            approveOrRejectEmailOrdersHandler(
              emailOrdersApprovalStatusOptions.Rejected
            )
          }
          autoFocus
          className={styles.rejectBtn}
        />
        {/* <Button
          icon="pi pi-refresh"
          label="Reprocess All"
          size="small"
          text
          autoFocus
          severity="secondary"
          aria-label="Reprocess All"
          onClick={() => onReProcessAllHandler(emailOrderData)}
          tooltip="Reprocess All - It reprocesses the failed orders due to errors in D365."
          className={styles.reProcessBtn}
        /> */}
      </div>
    </div>
  );

  const handleSelectAll = (e) => {
    if (e.checked) {
      const allIds = emailOrderData
        .filter(
          (item) =>
            item.approvalStatus === emailOrdersApprovalStatusOptions.Pending
        )
        .map((item) => item.id);

      setSelectedEmailOrders(allIds);
    } else {
      setSelectedEmailOrders([]);
    }
  };

  const headerCheckbox = (
    <Checkbox
      onChange={handleSelectAll}
      checked={
        selectedEmailOrders.length > 0 &&
        selectedEmailOrders.length ===
          emailOrderData.filter(
            (item) =>
              item.approvalStatus === emailOrdersApprovalStatusOptions.Pending
          ).length
      }
      onClick={(e) => e.stopPropagation()}
    />
  );

  const onCheckboxChange = (e, rowData) => {
    let newSelectedIds = [...selectedEmailOrders];
    if (e.checked) {
      if (!newSelectedIds.includes(rowData.id)) {
        newSelectedIds.push(rowData.id);
      }
    } else {
      newSelectedIds = newSelectedIds.filter((id) => id !== rowData.id);
    }
    setSelectedEmailOrders(newSelectedIds);
  };

  const checkboxColumnTemplate = (rowData) => {
    if (rowData?.approvalStatus === emailOrdersApprovalStatusOptions.Pending) {
      return (
        <Checkbox
          onChange={(e) => onCheckboxChange(e, rowData)}
          checked={selectedEmailOrders.includes(rowData.id)}
          onClick={(e) => e.stopPropagation()}
        />
      );
    }
  };

  const d365StatusFormat = (rowData) => {
    let severityValue = "";
    if (!rowData?.d365Status?.trim()) return;
    switch (rowData.d365Status) {
      case emailOrdersD365StatusOptions.Enqueued:
        severityValue = "secondary";
        break;
      case emailOrdersD365StatusOptions.InProcess:
        severityValue = "info";
        break;
      case emailOrdersD365StatusOptions.ProcessedWithErrors:
        severityValue = "danger";
        break;
      case emailOrdersD365StatusOptions.Processed:
        severityValue = "success";
        break;
      default:
        severityValue = "warning";
        break;
    }

    return (
      <div>
        {rowData.approvalStatus !== emailOrdersApprovalStatusOptions.Pending &&
          rowData.approvalStatus !==
            emailOrdersApprovalStatusOptions.Rejected && (
            <Tag
              severity={severityValue}
              value={rowData.d365Status}
              rounded
            ></Tag>
          )}
      </div>
    );
  };

  const initialValues = useMemo(
    () => ({
      purchaseOrderNumber: null,
      tradingPartnerName: null,
      approvalStatus: { label: "All", value: null },
      requestedShippingStartDate: new Date(),
      requestedShippingEndDate: new Date(),
      region: defaultRegion ? defaultRegion : regions[0]
    }),
    []
  );

  const validationSchema = Yup.object({
    purchaseOrderNumber: Yup.string().nullable(),
    tradingPartnerName: Yup.mixed().nullable(),
    approvalStatus: Yup.mixed().nullable(),
    requestedShippingStartDate: Yup.date().nullable(),
    requestedShippingEndDate: Yup.date()
      .nullable()
      .when("requestedShippingStartDate", {
        is: (start) => !!start,
        then: (schema) =>
          schema.required("End date is required when start date is selected"),
        otherwise: (schema) => schema.nullable()
      }),

    region: Yup.object().nullable().required("Region is required")
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setCurrentPage(0);
      setCurrentPageRows(10);
      setShowSpinner(true);
      setSelectedEmailOrders([]);
      fetchEmailOrders(values);
      setExpandedRows(null);
      setAllExpanded(false);
    }
  });

  const formRef = useRef(formik);

  useEffect(() => {
    if (regions.length == 0) return;
    if (userData?.regionId != null) {
      let filteredRegion = regions?.filter(
        (item) => item?.regionId == userData?.regionId
      );
      formik.setFieldValue("region", filteredRegion[0]);
      fetchTradingPartnersList(filteredRegion[0]?.regionNameAbbr);
      setDefaultRegion(filteredRegion[0]);
      fetchEmailOrders({ ...initialValues, region: filteredRegion[0] });
    } else {
      formik.setFieldValue("region", regions[0]);
      fetchTradingPartnersList(regions[0]?.regionNameAbbr);
      fetchEmailOrders({ ...initialValues, region: regions[0] });
    }
  }, [regions]);

  return (
    <>
      {showSpinner && <Spinner />}

      <ConfirmDialog
        visible={approveOrRejectOrderConfirmDialog}
        onHide={approveOrRejectEmailOrdersReject}
        message={
          "Are you sure you want to " +
          (emailOrderAction === emailOrdersApprovalStatusOptions.Approved
            ? "approve "
            : "reject ") +
          selectedEmailOrders.length +
          " orders?"
        }
        header="Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={approveOrRejectEmailOrdersAccept}
        reject={approveOrRejectEmailOrdersReject}
      />

      <Dialog
        visible={emailContentModalVisible}
        modal
        header="Original Content"
        style={{ width: "70vw", height: "80vh" }}
        breakpoints={{ "1024px": "75vw", "641px": "100vw" }}
        onHide={onHideEmailContentModal}
        maximizable
      >
        {orderType === orderTypeOptions.EmailBody ? (
          <div dangerouslySetInnerHTML={{ __html: emailContent }} />
        ) : orderType === orderTypeOptions.PDF ? (
          <iframe src={emailContent} width="100%" height="100%"></iframe>
        ) : null}
      </Dialog>

      <br></br>

      <form onSubmit={formik.handleSubmit} ref={formRef}>
        <div className="grid p-fluid">
          <div className="col-12 md:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="Region">
                <span className={styles.formLabel}>Region</span>
              </label>
            </div>
            <Dropdown
              id="Region"
              filter
              options={regions}
              optionLabel="regionNameAbbr"
              placeholder="Region"
              onChange={async (e) => {
                formik.setFieldValue("region", e.value);
                formik.setFieldValue("tradingPartnerName", null);
                setTradingPartners([]);
                if (e?.value?.regionNameAbbr) {
                  await fetchTradingPartnersList(e.value.regionNameAbbr);
                }
              }}
              value={formik.values.region}
              className={styles.dropdown}
              onBlur={formik.handleBlur}
            />
            {formik.touched.region && formik.errors.region && (
              <small className="p-error">{formik.errors.region}</small>
            )}
          </div>

          <div className="col-12 md:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="tradingPartnerName">
                <span className={styles.formLabel}>Trading Partner</span>
              </label>
            </div>
            <Dropdown
              id="tradingPartnerName"
              filter
              showClear
              placeholder="Trading Partner"
              options={tradingPartners}
              onChange={(e) => {
                formik.setFieldValue("tradingPartnerName", e?.value ?? null);
              }}
              value={formik.values.tradingPartnerName ?? null}
              className={styles.dropdown}
              onBlur={formik.handleBlur}
            />
          </div>

          <div className="col-12 md:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="approvalStatus">
                <span className={styles.formLabel}>Approval Status</span>
              </label>
            </div>
            <Dropdown
              id="approvalStatus"
              filter
              placeholder="Select Approval Status"
              value={formik.values?.approvalStatus}
              className={styles.dropdown}
              options={emailOrdersApprovalStatusFilterDropdownOptions}
              onChange={(e) => {
                formik.setFieldValue("approvalStatus", e.value);
              }}
            />
          </div>

          <div className="col-12 md:col-2">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="purchaseOrderNumber">
                <span className={styles.formLabel}>PO Number</span>
              </label>
            </div>
            <div className="p-inputgroup">
              <span className="p-inputgroup-addon">
                <i className="pi pi-search"></i>
              </span>
              <InputText
                id="purchaseOrderNumber"
                placeholder="PO Number"
                value={formik.values?.purchaseOrderNumber}
                onChange={formik.handleChange}
                className={`${styles.noBorder}`}
              />
            </div>
          </div>

          <div className="col-12 md:col-4">
            <div className={styles?.formLabelContainer}>
              <label htmlFor="requestedShippingStartDate">
                <span className={styles.formLabel}>Email Received Date</span>
              </label>
            </div>
            <div className="flex gap-2">
              <Calendar
                id="requestedShippingStartDate"
                value={formik.values?.requestedShippingStartDate}
                onChange={formik.handleChange}
                disabled={disableFields}
                placeholder="Start Date"
                maxDate={formik.values?.requestedShippingEndDate}
                className="w-full"
              />
              <Calendar
                id="requestedShippingEndDate"
                value={formik.values?.requestedShippingEndDate}
                onChange={formik.handleChange}
                disabled={disableFields}
                placeholder="End Date"
                minDate={formik.values?.requestedShippingStartDate}
                className="w-full"
              />
            </div>
            {formik.touched.requestedShippingEndDate &&
              formik.errors.requestedShippingEndDate && (
                <small className="p-error">
                  {formik.errors.requestedShippingEndDate}
                </small>
              )}
          </div>
        </div>

        <div
          className="flex justify-content-end flex-wrap gap-3"
          style={{ marginTop: "1rem", marginBottom: "0.3rem" }}
        >
          <div className="flex align-items-center justify-content-center">
            <Button
              label="Search"
              type="submit"
              size="medium"
              className={styles.primaryBtn}
            />
          </div>
          <div className="flex align-items-center justify-content-center">
            <Button
              label="Reset"
              type="button"
              size="medium"
              onClick={() => {
                setSelectedEmailOrders([]);
                setTradingPartners([]);
                formik.setValues({
                  purchaseOrderNumber: "",
                  tradingPartnerName: null,
                  approvalStatus: { label: "All", value: null },
                  requestedShippingStartDate: new Date(),
                  requestedShippingEndDate: new Date(),
                  region: defaultRegion ? defaultRegion : regions[0]
                });
                fetchEmailOrders({
                  purchaseOrderNumber: "",
                  tradingPartnerName: null,
                  approvalStatus: { label: "All", value: null },
                  requestedShippingStartDate: new Date(),
                  requestedShippingEndDate: new Date(),
                  region: defaultRegion ? defaultRegion : regions[0]
                });
                fetchTradingPartnersList(regions[0]?.regionNameAbbr);
                setDisableFields(false);
              }}
              className={styles.resetBtn}
            />
          </div>
        </div>
        <div className="flex justify-content-end ">
          <div className={styles.recordLimit}>
            <span>
              <i
                className={`${styles.recordLimit} pi pi-info-circle p-0`}
                style={{ fontSize: "10px", marginBottom: "0.5rem" }}
              ></i>{" "}
              Fetching latest 5000 records only
            </span>
          </div>
        </div>
      </form>

      <br></br>

      <DataTable
        value={emailOrderData}
        tableStyle={{ minWidth: "50rem" }}
        scrollable
        paginator={constants.paginator}
        rowsPerPageOptions={constants.rowsPerPageOptions}
        paginatorTemplate={constants.paginatorTemplate}
        currentPageReportTemplate={constants.currentPageReportTemplate}
        emptyMessage={constants.emptyMessage}
        dataKey="id"
        rows={currentPageRows}
        first={currentPage}
        onPage={(e) => {
          setCurrentPage(e.first);
          setCurrentPageRows(e?.rows);
        }}
        scrollHeight="30vw"
        expandedRows={expandedRows}
        onRowToggle={(e) => setExpandedRows(e.data)}
        rowExpansionTemplate={rowExpansionTemplate}
        footer={dataTableFooter}
      >
        {isReprocessBtnEnabled && (
          <Column style={{ width: "10px" }} body={orderActionTemplate} />
        )}
        <Column
          headerStyle={{ width: "4rem" }}
          header={headerCheckbox}
          body={checkboxColumnTemplate}
        ></Column>

        <Column
          header={
            <Button
              className={allExpanded ? "pi pi-angle-down" : "pi pi-angle-right"}
              style={{
                fontSize: "1.3rem",
                color: "#6c757d",
                backgroundColor: "#f8f9fa",
                border: "none",
                padding: "2px"
              }}
              onClick={toggleRows}
            ></Button>
          }
          style={{ width: "10px" }}
          expander={true}
        />

        {emailOrderColumns.map((col, index) => (
          <Column
            key={index}
            field={col.field}
            header={col.header}
            sortable={col.sort}
            style={{ minWidth: col.minWidth }}
            body={
              col.field.toLocaleLowerCase() === "purchaseordernumber"
                ? poNumberBody
                : col.field.toLocaleLowerCase() === "requestedshippingdate"
                  ? requestedShippingDateBody
                  : col.field.toLocaleLowerCase() === "requestedreceiptdate"
                    ? requestedReceiptDateBody
                    : col.field.toLocaleLowerCase() === "content"
                      ? emailContentFormat
                      : col.field.toLocaleLowerCase() === "approvalstatus"
                        ? processingStatusFormat
                        : col.field.toLocaleLowerCase() === "actiontakendateutc"
                          ? actionTakenDateUTCFormat
                          : col.field.toLocaleLowerCase() ===
                              "emailrecieveddatetime"
                            ? emailReceivedTimeFormat
                            : col.field.toLocaleLowerCase() === "d365status"
                              ? d365StatusFormat
                              : col.value
            }
          />
        ))}
      </DataTable>

      <Toast ref={toast} position="center" />
      {showReProcessConfirmation && (
        <ConfirmDialog
          visible={showReProcessConfirmation}
          message={"Are you sure you want to Re-process the order"}
          header="Note"
          icon="pi pi-exclamation-circle"
          style={{ width: "50vw", color: "red" }}
          breakpoints={{ "1100px": "75vw", "960px": "100vw" }}
          accept={() => reprocessItem()}
          onHide={() => {
            setShowReprocesConfirmation(false);
            setSelectedEmailOrders([]);
          }}
        ></ConfirmDialog>
      )}
      {showSaveConfirmation && (
        <ConfirmDialog
          visible={showSaveConfirmation}
          message={
            "Are you sure you want to save the changes to order line items?"
          }
          header="Confirm Save"
          icon="pi pi-exclamation-circle"
          style={{ width: "50vw", color: "red" }}
          accept={() => saveEditedItems()}
          onHide={() => setShowSaveConfirmation(false)}
        ></ConfirmDialog>
      )}
    </>
  );
};

export default EmailOrder;
